import React, { useContext, useEffect, useState, useCallback } from "react";
import { useAuth } from "hooks/Auth";
import { apiGet } from "helpers/Request";
import { transform as assignmentTransformer } from "schemas/Assignment";
import { getPaginationMeta } from "schemas/Util";
import MainBlock from "components/DesignSystem/LayoutBlocks/MainBlock";
import { useLocation, useHistory } from "react-router-dom";
import Pagination from "components/General/Pagination";
import { AppContext } from "context/App";
import { AssignmentContext } from "context/Assignment";
import AssignmentListToolbar from "Screens/PlannerScreen/AssignmentListToolbar";
import AssignmentTable from "./AssignmentTable";
import AssignmentCalendar from "./AssignmentCalendar";
import ButtonGroup from "components/Buttons/ButtonGroup";
import { CalendarIcon, ViewListIcon } from "@heroicons/react/outline";
import { useSelector } from "react-redux";
import { forceInt, packClasses } from "Utils";
import { handleShowFeed } from "./CalendarFeedModal";

export default function IndexScreen(props) {
    const auth = useAuth();
    const location = useLocation();
    const history = useHistory();
    const { workflows, settings } = useSelector((state) => state?.contentFlow);
    const { setCurrentNav, setPlannerNav } = useContext(AppContext);

    const [loading, setLoading] = useState(true);
    const [items, setItems] = useState([]);
    const [selectedItems, setSelectedItems] = useState([]);
    const [paginationMeta, setPaginationMeta] = useState({});
    const [isFiltered, setIsFiltered] = useState(false);
    const [queryParams, setQueryParams] = useState(
        new URLSearchParams(location.search)
    );

    const setSelected = (e, id) => {
        if (e.target.checked) {
            setSelectedItems([...selectedItems, id]);
        } else {
            setSelectedItems(selectedItems.filter((item) => item !== id));
        }
    };

    const updateParams = (key, val = null) => {
        const params = new URLSearchParams(location.search);

        if (typeof key === "object") {
            Object.entries(key).forEach(([k, v]) => {
                if (v === null) {
                    params.delete(k);
                } else {
                    params.set(k, v);
                }
            });
        } else {
            if (params.has(key) && params.get(key) === val) {
                params.delete(key);
            } else {
                params.set(key, val);
            }
        }

        history.replace(location.pathname + "?" + params.toString());
    };

    const onResetFilters = () => {
        history.replace(location.pathname + "?");
    };

    const load = useCallback(() => {
        setLoading(true);
        setItems([]);
        setSelectedItems([]);
        const defaultParams = {
            per_page: 40,
            dir: "desc",
            order: "due_at",
            include: ["issue"],
        };
        const definedParams = Object.fromEntries(
            new URLSearchParams(location.search)
        );

        apiGet(
            "/v4/assignment",
            auth.publication,
            Object.assign({}, defaultParams, definedParams)
        ).then((res) => {
            setItems(assignmentTransformer(res));
            setPaginationMeta(getPaginationMeta(res));
            setLoading(false);
        });
    }, [location, auth.publication]);

    useEffect(() => {
        load();
        const params = new URLSearchParams(location.search);
        setQueryParams(params);
        setPlannerNav(location.pathname + "?" + params.toString());
    }, [location, load, setPlannerNav]);

    useEffect(() => {
        setCurrentNav("planner");
    }, [setCurrentNav]);

    useEffect(() => {
        setIsFiltered(
            queryParams.has("open") ||
                queryParams.has("assigned_to") ||
                queryParams.has("mentions") ||
                queryParams.has("workflow")
        );
    }, [queryParams]);

    return (
        <MainBlock>
            <AssignmentContext.Provider
                value={{
                    items,
                    selectedItems,
                    loading,
                    load,
                    updateParams,
                    queryParams,
                }}
            >
                <AssignmentListToolbar queryParams={queryParams} />
                <div className="my-4 flex flex-wrap justify-center gap-2 sm:justify-start">
                    <div>
                        <ButtonGroup
                            variant="xs"
                            buttons={[
                                <button
                                    type="button"
                                    key="filter-view-cal"
                                    selected={
                                        queryParams.get("view") === "calendar"
                                    }
                                    onClick={() =>
                                        updateParams("view", "calendar")
                                    }
                                >
                                    <CalendarIcon className="h-6 w-6 sm:h-4 sm:w-4" />
                                    <span className="sr-only">
                                        View Calendar
                                    </span>
                                </button>,
                                <button
                                    type="button"
                                    key="filter-view-tbl"
                                    selected={
                                        queryParams.get("view") !== "calendar"
                                    }
                                    onClick={() =>
                                        updateParams({
                                            view: "table",
                                            range: null,
                                        })
                                    }
                                >
                                    <ViewListIcon className="h-6 w-6 sm:h-4 sm:w-4" />
                                    <span className="sr-only">View Table</span>
                                </button>,
                            ]}
                        />
                    </div>
                    <div>
                        <ButtonGroup
                            variant="xs"
                            label="Status"
                            buttons={[
                                <button
                                    type="button"
                                    key="filter-view-table"
                                    selected={
                                        parseInt(queryParams.get("open")) !==
                                            1 &&
                                        parseInt(queryParams.get("closed")) !==
                                            1
                                    }
                                    onClick={() =>
                                        updateParams({
                                            closed: null,
                                            open: null,
                                        })
                                    }
                                >
                                    Any
                                </button>,
                                <button
                                    type="button"
                                    selected={
                                        parseInt(queryParams.get("open")) === 1
                                    }
                                    onClick={() =>
                                        updateParams({
                                            open: 1,
                                            closed: null,
                                        })
                                    }
                                >
                                    Open
                                </button>,
                                <button
                                    type="button"
                                    key="filter-view-table"
                                    selected={
                                        parseInt(queryParams.get("closed")) ===
                                        1
                                    }
                                    onClick={() =>
                                        updateParams({
                                            closed: 1,
                                            open: null,
                                        })
                                    }
                                >
                                    Completed
                                </button>,
                            ]}
                        />
                    </div>
                    <div>
                        <ButtonGroup
                            variant="xs"
                            buttons={[
                                <button
                                    type="button"
                                    selected={!queryParams.get("assigned_to")}
                                    onClick={() =>
                                        updateParams({
                                            assigned_to: null,
                                            mentions: null,
                                        })
                                    }
                                >
                                    Any
                                </button>,
                                <button
                                    type="button"
                                    selected={
                                        queryParams.get("assigned_to") ===
                                        auth.user.uuid
                                    }
                                    onClick={() =>
                                        updateParams({
                                            assigned_to: auth.user.uuid,
                                            mentions: auth.user.uuid,
                                            open: 1,
                                        })
                                    }
                                >
                                    Mine
                                </button>,
                            ]}
                        />
                    </div>
                    <div>
                        <ButtonGroup
                            variant="xs"
                            buttons={[
                                <button
                                    type="menu"
                                    buttons={workflows.map((wf, index) => (
                                        <button
                                            key={index}
                                            onClick={() => {
                                                updateParams("workflow", wf.id);
                                            }}
                                        >
                                            {wf.name}
                                        </button>
                                    ))}
                                >
                                    {queryParams.has("workflow")
                                        ? workflows.find(
                                              (wf) =>
                                                  wf.id ===
                                                  forceInt(
                                                      queryParams.get(
                                                          "workflow"
                                                      )
                                                  )
                                          )?.name
                                        : "Workflow"}
                                </button>,
                            ]}
                        />
                    </div>
                    <div>
                        <ButtonGroup
                            variant="xs"
                            buttons={[
                                <button
                                    type="button"
                                    onClick={() => {
                                        handleShowFeed(auth, settings);
                                    }}
                                >
                                    Calendar Feed
                                </button>,
                            ]}
                        />
                    </div>
                    <div className={isFiltered ? "" : "hidden"}>
                        <ButtonGroup
                            variant="xs"
                            buttons={[
                                <button
                                    type="button"
                                    key="reset-filters"
                                    onClick={onResetFilters}
                                >
                                    Reset
                                </button>,
                            ]}
                        />
                    </div>
                    <div className="md:grid md:flex-grow md:justify-items-end">
                        <Pagination
                            variant="xs"
                            maxPages={3}
                            basePath="planner"
                            {...paginationMeta}
                        />
                    </div>
                </div>
                {queryParams.has("view") &&
                queryParams.get("view") === "calendar" ? (
                    <AssignmentCalendar
                        addSelected={setSelected}
                        setAllSelected={setSelectedItems}
                        queryParams={queryParams}
                    />
                ) : (
                    <AssignmentTable
                        addSelected={setSelected}
                        setAllSelected={setSelectedItems}
                        queryParams={queryParams}
                    />
                )}
            </AssignmentContext.Provider>

            <div
                className={packClasses(
                    "my-4",
                    queryParams.get("view") === "calendar" ? "hidden" : ""
                )}
            >
                <Pagination basePath="planner" {...paginationMeta} />
            </div>
        </MainBlock>
    );
}
